/**
 * MicButton: 마이크 토글 + 디바이스 선택 드롭다운.
 *
 * v1 원본: RenewalComponents/Buttons/MicButton/MicButtonPresenter.js
 * v1 CSS: .rtc_new3 .newHeader_btn > .newHeader_subBtn + .newHeader_subBtn--dropdown
 *
 * 기능:
 * - SNTooltip: 마이크 off 시 "스페이스바를 누르고있는동안 마이크가 켜집니다." 5초간 노출 (회의 진입 후 1회만)
 * - PTT(Push-to-Talk): 스페이스바 길게 누르면 일시적으로 마이크 온, 떼면 원복
 *   (AudioTab의 isSpaceUnmute 설정이 활성화된 경우에만 동작)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector } from '@/store/hooks';
import SNTooltip from '@luna-sena/sena-tooltip';

interface MicButtonProps {
  onToggleAudio: () => void;
  onChangeDevice?: (type: string, deviceId: string) => void;
}

const MicButton: React.FC<MicButtonProps> = ({ onToggleAudio, onChangeDevice }) => {
  const local = useAppSelector((s) => s.local);
  const isMuted = local.audioMuted;
  const isSpaceUnmute = local.isSpaceUnmute;
  const audioInputDevices = local.audioInputDevices;
  const audioInputDeviceId = local.audioInputDeviceId;
  const isConnected = local.isConnected;
  const floor = useAppSelector((s) => s.floor);

  const [isListOpen, setIsListOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── SNTooltip 상태 ─────────────────────────────────────────
  const [showTooltip, setShowTooltip] = useState(false);
  // 해당 회의에서 1회만 노출되도록 추적
  const hasShownTooltipRef = useRef(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── PTT 원래 마이크 상태 저장용 ────────────────────────────
  const originalMicMutedRef = useRef<boolean | null>(null);
  const isPttActiveRef = useRef(false);

  // 발언권 모드에서 발언권 없으면 비활성화
  const isFloorBlocked = floor.isEnabled && !floor.isGranted && !floor.isRequesting;
  const isEnabled = !isMuted && !isFloorBlocked;

  // ── 툴팁 노출 조건 ──────────────────────────────────────────
  // 조건: 1) isSpaceUnmute ON  2) 회의당 1회만  3) 5초간  4) 마이크 OFF
  useEffect(() => {
    // 조건 미충족 → 표시하지 않음
    if (!isSpaceUnmute || !isConnected || !isMuted || hasShownTooltipRef.current) {
      return;
    }

    // 모든 조건 충족 → 툴팁 표시
    hasShownTooltipRef.current = true;
    setShowTooltip(true);

    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(false);
      tooltipTimerRef.current = null;
    }, 5000);

    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
    };
  }, [isSpaceUnmute, isConnected, isMuted]);

  // 마이크가 켜지면 즉시 툴팁 숨기기
  useEffect(() => {
    if (!isMuted && showTooltip) {
      setShowTooltip(false);
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
    }
  }, [isMuted, showTooltip]);

  // ── 외부 클릭 닫기 ─────────────────────────────────────────
  useEffect(() => {
    if (!isListOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isListOpen]);

  // ── 키보드 단축키: Ctrl+Shift+M ────────────────────────────
  const handleToggle = useCallback(() => {
    if (!isFloorBlocked) onToggleAudio();
  }, [isFloorBlocked, onToggleAudio]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        handleToggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleToggle]);

  // ── PTT (Push-to-Talk): 스페이스바 길게 누르기 ─────────────
  useEffect(() => {
    if (!isSpaceUnmute) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에서는 PTT 동작 안 함
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.key === ' ' && !e.repeat) {
        e.preventDefault();

        if (!isPttActiveRef.current && isMuted) {
          isPttActiveRef.current = true;
          originalMicMutedRef.current = isMuted;
          onToggleAudio();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();

        if (isPttActiveRef.current) {
          isPttActiveRef.current = false;
          if (originalMicMutedRef.current) {
            onToggleAudio();
          }
          originalMicMutedRef.current = null;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpaceUnmute, isMuted, onToggleAudio]);

  const handleSelectDevice = (deviceId: string) => {
    onChangeDevice?.('audioinput', deviceId);
    setIsListOpen(false);
  };

  // ── 마이크 버튼 내부 콘텐츠 ─────────────────────────────────
  const micBtnContent = (
    <div className="newHeader_btn">
      <button
        type="button"
        className={`newHeader_subBtn${!isEnabled ? ' disabled' : ''}`}
        onClick={handleToggle}
        disabled={isFloorBlocked}
        onKeyDown={(e) => {
          if (e.key === ' ') {
            e.preventDefault();
          }
        }}
      >
        <span className="newHeader_btnIcon icoMic" />
      </button>
      <button
        type="button"
        className="newHeader_subBtn newHeader_subBtn--dropdown"
        onClick={() => setIsListOpen(!isListOpen)}
      >
        <p className="newHeader_btnText">마이크</p>
      </button>
    </div>
  );

  return (
    <div ref={containerRef} className={`newHeader_btnItem${isListOpen ? ' open' : ''}`}>
      {/* 툴팁 표시 중일 때만 SNTooltip 렌더링, 아닐 때는 plain div로 마우스 hover 툴팁 방지 */}
      {showTooltip ? (
        <SNTooltip
          label={<>스페이스바를 누르고있는동안<br />마이크가 켜집니다.</>}
          open={true}
        >
          {micBtnContent}
        </SNTooltip>
      ) : (
        micBtnContent
      )}
      {isListOpen && (
        <div className="viewerHeader__layer">
          <strong className="viewerHeader__layerTitle">마이크 선택</strong>
          <ul className="viewerHeader__layerList">
            {audioInputDevices.map((device) => (
              <li key={device.deviceId}>
                <button
                  type="button"
                  className={`viewerHeader__layerListBtn${device.deviceId === audioInputDeviceId ? ' selected' : ''}`}
                  onClick={() => handleSelectDevice(device.deviceId)}
                >
                  {device.label || `마이크 ${device.deviceId.slice(0, 5)}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MicButton;

